import { createAsyncThunk } from "@reduxjs/toolkit";
import { serviceModulesApi } from "../../api/serviceModulesApi";
import { AbsenceServiceModule, CalendarEvent, CalendarServiceModule, FileServiceModule, LeaderboardEntry, LeaderboardServiceModule, LeaderboardTable, ServiceModule, TextServiceModule, TimeTrackingServiceModule, UrlServiceModule } from "../../types/serviceModuleTypes";
import { setNewestServiceModule } from "../reducers/serviceModulesReducer";
import { PaginationParameters } from "../../api/Pagination";
import { AppDispatch, RootState } from "../store";
import { handleRequest } from "../utils/handleRequest";
import i18n from "../../i18n";

export const fetchServiceModulesThunk = createAsyncThunk<
  ServiceModule[] | null,
  void,
  { dispatch: AppDispatch; state: RootState }
>("serviceModules/fetchServiceModules", async (_, { dispatch }) => {
  const res = await handleRequest(dispatch, "indirect", () =>
    serviceModulesApi.getAllServiceModules()
  );
  return res.success() ? res.result : null;
});

// Create / Update / Delete Modules
export const createServiceModuleThunk = createAsyncThunk<
  ServiceModule,
  { serviceModuleType: number; parentFolderServiceModuleId?: number },
  { dispatch: AppDispatch; state: RootState }
>("serviceModules/createServiceModule", async (params, { dispatch }) => {
  const res = await handleRequest(
    dispatch,
    "direct",
    () => serviceModulesApi.createServiceModule(params),
    i18n.t("requestMessage.serviceModuleCreated")
  );
  if (!res.success() || !res.result) {
    throw new Error(res.errorMessage ?? "Failed to create service module");
  }
  dispatch(setNewestServiceModule(res.result));
  await dispatch(fetchServiceModulesThunk());
  return res.result as ServiceModule;
});

export const updateServiceModuleOrderThunk = createAsyncThunk<
  number[],
  number[],
  { dispatch: AppDispatch; state: RootState }
>("serviceModules/updateServiceModuleOrder", async (orderedIds, { dispatch }) => {
  const res = await handleRequest(
    dispatch,
    "direct",
    () => serviceModulesApi.updateServiceModuleOrder(orderedIds),
    i18n.t("requestMessage.serviceModuleOrderUpdated")
  );
  if (!res.success()) {
    throw new Error(res.errorMessage ?? "Failed to update service module order");
  }
  await dispatch(fetchServiceModulesThunk());
  return orderedIds;
});

export const updateServiceModuleThunk = createAsyncThunk<
  ServiceModule,
  { serviceModuleId: number; data: Partial<ServiceModule> },
  { dispatch: AppDispatch; state: RootState }
>("serviceModules/updateServiceModule", async ({ serviceModuleId, data }, { dispatch }) => {
  const res = await handleRequest(
    dispatch,
    "direct",
    () => serviceModulesApi.updateServiceModule(serviceModuleId, data),
    i18n.t("requestMessage.serviceModuleUpdated")
  );
  if (!res.success() || !res.result) {
    throw new Error(res.errorMessage ?? "Failed to update service module");
  }
  await dispatch(fetchServiceModulesThunk());
  return res.result as ServiceModule;
});

export const deleteServiceModuleThunk = createAsyncThunk<
  number,
  number,
  { dispatch: AppDispatch; state: RootState }
>("serviceModules/deleteServiceModule", async (serviceModuleId, { dispatch }) => {
  const res = await handleRequest(
    dispatch,
    "direct",
    () => serviceModulesApi.deleteServiceModule(serviceModuleId),
    i18n.t("requestMessage.serviceModuleDeleted")
  );
  if (!res.success()) {
    throw new Error(res.errorMessage ?? "Failed to delete service module");
  }
  return serviceModuleId;
});

// Calendar Modules
export const fetchCalendarModuleThunk = createAsyncThunk<
  CalendarServiceModule | null,
  number,
  { dispatch: AppDispatch; state: RootState }
>("serviceModules/fetchCalendarModule", async (serviceModuleId, { dispatch }) => {
  const res = await handleRequest(dispatch, "indirect", () =>
    serviceModulesApi.getCalendarModule(serviceModuleId)
  );
  return res.success() ? res.result : null;
});

export const updateCalendarModuleThunk = createAsyncThunk<
  CalendarServiceModule,
  { serviceModuleId: number; data: Partial<CalendarServiceModule> },
  { dispatch: AppDispatch; state: RootState }
>("serviceModules/updateCalendarModule", async ({ serviceModuleId, data }, { dispatch }) => {
  const res = await handleRequest(
    dispatch,
    "direct",
    () => serviceModulesApi.updateCalendarModule(serviceModuleId, data),
    i18n.t("requestMessage.calendarModuleUpdated")
  );
  if (!res.success() || !res.result) {
    throw new Error(res.errorMessage ?? "Failed to update calendar module");
  }
  await dispatch(fetchServiceModulesThunk());
  return res.result as CalendarServiceModule;
});

export const fetchCalendarEventsThunk = createAsyncThunk<
  CalendarEvent[] | null,
  { serviceModuleId: number; startDate: Date; endDate: Date },
  { dispatch: AppDispatch; state: RootState }
>("calendar/fetchEvents", async ({ serviceModuleId, startDate, endDate }, { dispatch }) => {
  const res = await handleRequest(dispatch, "indirect", () =>
    serviceModulesApi.getCalendarEvents(serviceModuleId, startDate, endDate)
  );
  return res.success() ? res.result : null;
});

export const createCalendarEventThunk = createAsyncThunk<
  CalendarEvent,
  { serviceModuleId: number; eventData: Partial<CalendarEvent> },
  { dispatch: AppDispatch; state: RootState }
>("calendar/createEvent", async ({ serviceModuleId, eventData }, { dispatch }) => {
  const res = await handleRequest(
    dispatch,
    "direct",
    () => serviceModulesApi.createCalendarEvent(serviceModuleId, eventData),
    i18n.t("requestMessage.calendarEventCreated")
  );
  if (res.success()) {
    return res.result as CalendarEvent;
  }
  throw new Error(res.errorMessage ?? "Failed to create calendar event");
});

export const updateCalendarEventThunk = createAsyncThunk<
  CalendarEvent,
  { serviceModuleId: number; eventId: number; eventData: Partial<CalendarEvent> },
  { dispatch: AppDispatch; state: RootState }
>("calendar/updateEvent", async ({ serviceModuleId, eventId, eventData }, { dispatch }) => {
  const res = await handleRequest(
    dispatch,
    "direct",
    () => serviceModulesApi.updateCalendarEvent(serviceModuleId, eventId, eventData),
    i18n.t("requestMessage.calendarEventUpdated")
  );
  if (!res.success() || !res.result) {
    throw new Error(res.errorMessage ?? "Failed to update calendar event");
  }
  return res.result as CalendarEvent;
});

export const deleteCalendarEventThunk = createAsyncThunk<
  number,
  { serviceModuleId: number; eventId: number },
  { dispatch: AppDispatch; state: RootState }
>("calendar/deleteEvent", async ({ serviceModuleId, eventId }, { dispatch }) => {
  const res = await handleRequest(
    dispatch,
    "direct",
    () => serviceModulesApi.deleteCalendarEvent(serviceModuleId, eventId),
    i18n.t("requestMessage.calendarEventDeleted")
  );
  if (!res.success()) {
    throw new Error(res.errorMessage ?? "Failed to delete calendar event");
  }
  return eventId;
});

// Thunks for folder and children management
export const fetchFolderModuleThunk = createAsyncThunk<
  ServiceModule[],
  number,
  { dispatch: AppDispatch; state: RootState }
>("serviceModules/fetchFolder", async (serviceModuleId, { dispatch }) => {
  const res = await handleRequest(dispatch, "indirect", () =>
    serviceModulesApi.getFolderChildren(serviceModuleId)
  );
  if (res.success()) return res.result as ServiceModule[];
  throw new Error(res.errorMessage ?? "Failed to fetch folder children");
});

export const updateFolderModuleThunk = createAsyncThunk<
  ServiceModule,
  { serviceModuleId: number; data: Partial<ServiceModule> },
  { dispatch: AppDispatch; state: RootState }
>("serviceModules/updateFolder", async ({ serviceModuleId, data }, { dispatch }) => {
  const res = await handleRequest(
    dispatch,
    "direct",
    () => serviceModulesApi.updateFolderModule(serviceModuleId, data),
    i18n.t("requestMessage.folderUpdated")
  );
  if (!res.success() || !res.result) {
    throw new Error(res.errorMessage ?? "Failed to update folder module");
  }
  return res.result as ServiceModule;
});

export const fetchFileModuleThunk = createAsyncThunk<
  FileServiceModule,
  number,
  { dispatch: AppDispatch; state: RootState }
>("serviceModules/fetchFileModule", async (serviceModuleId, { dispatch }) => {
  const res = await handleRequest(dispatch, "indirect", () =>
    serviceModulesApi.getFileModule(serviceModuleId)
  );
  if (res.success()) return res.result as FileServiceModule;
  throw new Error(res.errorMessage ?? "Failed to fetch file module");
});

export const updateFileModuleThunk = createAsyncThunk<
  ServiceModule,
  { serviceModuleId: number; data: Partial<ServiceModule> },
  { dispatch: AppDispatch; state: RootState }
>("serviceModules/updateFile", async ({ serviceModuleId, data }, { dispatch }) => {
  const res = await handleRequest(
    dispatch,
    "direct",
    () => serviceModulesApi.updateFileModuleMetadata(serviceModuleId, data),
    i18n.t("requestMessage.fileMetadataUpdated")
  );
  if (!res.success() || !res.result) {
    throw new Error(res.errorMessage ?? "Failed to update file module");
  }
  return res.result as ServiceModule;
});

export const uploadFileThunk = createAsyncThunk<
  ServiceModule,
  { serviceModuleId: number; fileData: { data: string; filename: string } },
  { dispatch: AppDispatch; state: RootState }
>("serviceModules/uploadFile", async ({ serviceModuleId, fileData }, { dispatch }) => {
  const res = await handleRequest(
    dispatch,
    "direct",
    () => serviceModulesApi.uploadFile(serviceModuleId, fileData),
    i18n.t("requestMessage.fileUploadedSimple")
  );
  if (!res.success() || !res.result) {
    throw new Error(res.errorMessage ?? "Failed to upload file");
  }
  return res.result as ServiceModule;
});

export const deleteFileThunk = createAsyncThunk<
  number,
  number,
  { dispatch: AppDispatch; state: RootState }
>("serviceModules/deleteFile", async (serviceModuleId, { dispatch }) => {
  const res = await handleRequest(
    dispatch,
    "direct",
    () => serviceModulesApi.deleteFile(serviceModuleId),
    i18n.t("requestMessage.fileDeleted")
  );
  if (!res.success()) {
    throw new Error(res.errorMessage ?? "Failed to delete file");
  }
  return serviceModuleId;
});

// Text module
export const fetchTextModuleThunk = createAsyncThunk<
  TextServiceModule | null,
  number,
  { dispatch: AppDispatch; state: RootState }
>("serviceModules/fetchTextModule", async (serviceModuleId, { dispatch }) => {
  const res = await handleRequest(dispatch, "indirect", () =>
    serviceModulesApi.getTextModule(serviceModuleId)
  );
  return res.success() ? res.result : null;
});

export const updateTextModuleThunk = createAsyncThunk<
  TextServiceModule,
  { serviceModuleId: number; data: Partial<TextServiceModule> },
  { dispatch: AppDispatch; state: RootState }
>("serviceModules/updateTextModule", async ({ serviceModuleId, data }, { dispatch }) => {
  const res = await handleRequest(
    dispatch,
    "direct",
    () => serviceModulesApi.updateTextModule(serviceModuleId, data),
    i18n.t("requestMessage.textModuleUpdated")
  );
  if (!res.success() || !res.result) {
    throw new Error(res.errorMessage ?? "Failed to update text module");
  }
  return res.result as TextServiceModule;
});

// URL module
export const fetchUrlModuleThunk = createAsyncThunk<
  UrlServiceModule | null,
  number,
  { dispatch: AppDispatch; state: RootState }
>("serviceModules/fetchUrlModule", async (serviceModuleId, { dispatch }) => {
  const res = await handleRequest(dispatch, "indirect", () =>
    serviceModulesApi.getUrlModule(serviceModuleId)
  );
  return res.success() ? res.result : null;
});

export const updateUrlModuleThunk = createAsyncThunk<
  UrlServiceModule,
  { serviceModuleId: number; data: Partial<UrlServiceModule> },
  { dispatch: AppDispatch; state: RootState }
>("serviceModules/updateUrlModule", async ({ serviceModuleId, data }, { dispatch }) => {
  const res = await handleRequest(
    dispatch,
    "direct",
    () => serviceModulesApi.updateUrlModule(serviceModuleId, data),
    i18n.t("requestMessage.urlModuleUpdated")
  );
  if (!res.success() || !res.result) {
    throw new Error(res.errorMessage ?? "Failed to update url module");
  }
  return res.result as UrlServiceModule;
});

// Leaderboard module
export const fetchLeaderboardModuleThunk = createAsyncThunk<
  LeaderboardServiceModule | null,
  number,
  { dispatch: AppDispatch; state: RootState }
>("serviceModules/fetchLeaderboardModule", async (serviceModuleId, { dispatch }) => {
  const res = await handleRequest(dispatch, "indirect", () =>
    serviceModulesApi.getLeaderboardModule(serviceModuleId)
  );
  return res.success() ? res.result : null;
});

export const updateLeaderboardModuleThunk = createAsyncThunk<
  LeaderboardServiceModule,
  { serviceModuleId: number; data: Partial<LeaderboardServiceModule> },
  { dispatch: AppDispatch; state: RootState }
>("serviceModules/updateLeaderboardModule", async ({ serviceModuleId, data }, { dispatch }) => {
  const res = await handleRequest(
    dispatch,
    "direct",
    () => serviceModulesApi.updateLeaderboardModule(serviceModuleId, data),
    i18n.t("requestMessage.leaderboardModuleUpdated")
  );
  if (!res.success() || !res.result) {
    throw new Error(res.errorMessage ?? "Failed to update leaderboard module");
  }
  return res.result as LeaderboardServiceModule;
});

export const fetchLeaderboardActionModulesThunk = createAsyncThunk<
  any[] | null,
  { isCompanyWideAssigned: boolean; groupIds: number[] },
  { dispatch: AppDispatch; state: RootState }
>("serviceModules/fetchLeaderboardActionModules", async (params, { dispatch }) => {
  const res = await handleRequest(dispatch, "indirect", () =>
    serviceModulesApi.getLeaderboardActionModules(params.isCompanyWideAssigned, params.groupIds)
  );
  return res.success() ? res.result : null;
});

export const fetchLeaderboardTableThunk = createAsyncThunk<
  LeaderboardTable,
  { pagination: PaginationParameters; serviceModuleId: number; actionModuleId?: number; date?: string },
  { dispatch: AppDispatch; state: RootState }
>("serviceModules/fetchLeaderboardData", async ({ pagination, serviceModuleId, actionModuleId, date }, { dispatch }) => {
  const res = await handleRequest(dispatch, "indirect", () =>
    serviceModulesApi.getLeaderboardData({ ...pagination, serviceModuleId, actionModuleId, date })
  );
  if (res.success()) return res.result as LeaderboardTable;
  throw new Error(res.errorMessage ?? "Failed to fetch leaderboard table");
});

export const fetchLeaderboardDashboardThunk = createAsyncThunk<
  LeaderboardEntry[],
  { serviceModuleId: number; actionModuleId: number },
  { dispatch: AppDispatch; state: RootState }
>("serviceModules/fetchLeaderboardDashboardData", async ({ serviceModuleId, actionModuleId }, { dispatch }) => {
  const res = await handleRequest(dispatch, "indirect", () =>
    serviceModulesApi.getLeaderboardDashboardData({ serviceModuleId, actionModuleId })
  );
  if (res.success()) return res.result as LeaderboardEntry[];
  throw new Error(res.errorMessage ?? "Failed to fetch leaderboard dashboard data");
});

// Timetracking module
export const fetchTimeTrackingModuleThunk = createAsyncThunk<
  TimeTrackingServiceModule | null,
  number,
  { dispatch: AppDispatch; state: RootState }
>("serviceModules/fetchTimeTrackingModule", async (serviceModuleId, { dispatch }) => {
  const res = await handleRequest(dispatch, "indirect", () =>
    serviceModulesApi.getTimeTrackingModule(serviceModuleId)
  );
  return res.success() ? res.result : null;
});

export const updateTimeTrackingModuleThunk = createAsyncThunk<
  TimeTrackingServiceModule,
  { serviceModuleId: number; data: Partial<TimeTrackingServiceModule> },
  { dispatch: AppDispatch; state: RootState }
>("serviceModules/updateTimeTrackingModule", async ({ serviceModuleId, data }, { dispatch }) => {
  const res = await handleRequest(
    dispatch,
    "direct",
    () => serviceModulesApi.updateTimeTrackingModule(serviceModuleId, data),
    i18n.t("requestMessage.timeTrackingModuleUpdated")
  );
  if (!res.success() || !res.result) {
    throw new Error(res.errorMessage ?? "Failed to update time tracking module");
  }
  await dispatch(fetchServiceModulesThunk());
  return res.result as TimeTrackingServiceModule;
});

export const updateAbsenceModuleThunk = createAsyncThunk<
  AbsenceServiceModule,
  { serviceModuleId: number; data: Partial<AbsenceServiceModule> },
  { dispatch: AppDispatch; state: RootState }
>("serviceModules/updateAbsenceModule", async ({ serviceModuleId, data }, { dispatch }) => {
  const res = await handleRequest(
    dispatch,
    "direct",
    () => serviceModulesApi.updateAbsenceModule(serviceModuleId, data),
    i18n.t("requestMessage.absenceModuleUpdated")
  );
  if (!res.success() || !res.result) {
    throw new Error(res.errorMessage ?? "Failed to update absence module");
  }
  await dispatch(fetchServiceModulesThunk());
  return res.result as AbsenceServiceModule;
});